// Global fetch + EventSource interceptors for quota handling + network diagnostics
// This file is imported once at startup (see index.js).

const NETWORK_BUFFER_KEY = '__uiCliNetworkBuffer';
const NETWORK_BUFFER_LIMIT = 600;

const getNetworkBuffer = () => {
  window[NETWORK_BUFFER_KEY] = window[NETWORK_BUFFER_KEY] || [];
  return window[NETWORK_BUFFER_KEY];
};

const pushNetworkEntry = (entry) => {
  try {
    const buffer = getNetworkBuffer();
    buffer.push({ ts: new Date().toISOString(), ...entry });
    if (buffer.length > NETWORK_BUFFER_LIMIT) {
      buffer.splice(0, buffer.length - NETWORK_BUFFER_LIMIT);
    }
  } catch {
    // Never break app behavior because of debug logging
  }
};

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const truncate = (value, maxLen = 12000) => {
  const text = String(value ?? '');
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
};

const extractMethodAndBody = (args) => {
  const input = args?.[0];
  const init = args?.[1] || {};
  const method = String(init?.method || 'GET').toUpperCase();

  let bodyPreview = null;
  const body = init?.body;
  if (typeof body === 'string') {
    bodyPreview = truncate(body, 20000);
  } else if (body instanceof URLSearchParams) {
    bodyPreview = truncate(body.toString(), 20000);
  } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
    bodyPreview = '[FormData]';
  } else if (body != null) {
    bodyPreview = truncate(safeJsonStringify(body), 20000);
  }

  const url = typeof input === 'string' ? input : String(input?.url || input);
  return { method, url, bodyPreview };
};

const headersToObject = (headers) => {
  try {
    const out = {};
    if (!headers) return out;
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        out[key] = value;
      });
      return out;
    }
    if (Array.isArray(headers)) {
      headers.forEach(([k, v]) => {
        out[k] = v;
      });
      return out;
    }
    return { ...headers };
  } catch {
    return {};
  }
};

/* ------------------------------------------------------------
 *  FETCH WRAPPER – catch HTTP statuses + log request/response
 * ----------------------------------------------------------*/
const originalFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const startedAt = Date.now();
  const { method, url, bodyPreview } = extractMethodAndBody(args);
  const requestHeaders = headersToObject(args?.[1]?.headers);

  pushNetworkEntry({
    type: 'fetch.request',
    requestId,
    method,
    url,
    requestHeaders,
    requestBodyPreview: bodyPreview,
  });

  let res;
  try {
    res = await originalFetch(...args);
  } catch (error) {
    pushNetworkEntry({
      type: 'fetch.error',
      requestId,
      method,
      url,
      durationMs: Date.now() - startedAt,
      errorName: error?.name,
      errorMessage: error?.message,
    });
    throw error;
  }

  try {
    const contentType = res.headers.get('content-type') || '';
    const responseHeaders = headersToObject(res.headers);

    let responseBodyPreview = null;
    try {
      const text = await res.clone().text();
      responseBodyPreview = truncate(text, 30000);
    } catch {
      responseBodyPreview = '[unavailable]';
    }

    pushNetworkEntry({
      type: 'fetch.response',
      requestId,
      method,
      url,
      status: res.status,
      ok: res.ok,
      durationMs: Date.now() - startedAt,
      contentType,
      responseHeaders,
      responseBodyPreview,
    });

    if (res.status === 429) {
      // Check if this is a user quota error vs LLM service error
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const clone = res.clone();
        const body = await clone.json().catch(() => null);

        // Only trigger quota modal for actual user quota errors
        // Not for LLM service errors (which should be 503 anyway)
        if (body?.error !== 'llm_service_unavailable') {
          console.log('[Fetch Interceptor] User quota exceeded (429)');
          window.dispatchEvent(new Event('quotaExceeded'));
        } else {
          console.log('[Fetch Interceptor] LLM service error (429), not triggering quota modal');
        }
      } else {
        // Non-JSON 429 response, assume it's a quota error
        window.dispatchEvent(new Event('quotaExceeded'));
      }
    } else if (res.status === 503) {
      // Service unavailable - likely LLM service error
      // Don't trigger quota modal for these
      console.log('[Fetch Interceptor] Service unavailable (503), not a quota issue');
    } else if (res.status === 401) {
      // Unauthorized - authentication required
      window.dispatchEvent(new CustomEvent('authRequired', {
        detail: { url: args[0], context: 'fetch', status: 401 }
      }));
    } else if (res.status === 500) {
      // Internal server error - check if it's explicitly NOT a quota issue
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const clone = res.clone();
        const body = await clone.json().catch(() => null);

        // Check if backend explicitly says it's not a quota issue
        if (body?.error === 'internal_server_error' && body?.message?.includes('not a quota issue')) {
          console.log('[Fetch Interceptor] Internal server error (500), not a quota issue');
          // Don't trigger quota modal
        }
      }
    } else if (res.status === 403) {
      // Check if this is a CORS error vs quota error
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const clone = res.clone();
        const body = await clone.json().catch(() => null);

        // Check for explicit CORS error
        if (body?.error === 'cors_error') {
          console.warn('[Fetch Interceptor] CORS error detected, not triggering quota modal:', body.message);
          // Don't trigger quota modal for CORS errors
          return res;
        }

        // Firebase callable error comes back with JSON body {error:{status:"RESOURCE_EXHAUSTED", ...}}
        if (body?.error?.status === 'RESOURCE_EXHAUSTED') {
          window.dispatchEvent(new Event('quotaExceeded'));
        }

        // Check for authentication errors
        if (body?.error?.status === 'UNAUTHENTICATED' || body?.error?.message?.includes('auth')) {
          window.dispatchEvent(new CustomEvent('authRequired', {
            detail: { url: args[0], context: 'fetch' }
          }));
        }
      }
    }

    // Check for soft prompt header to show sales modal
    if (res.headers.get('x-sales-prompt') === '1') {
      window.dispatchEvent(new Event('salesPrompt'));
    }
  } catch (_) {
    // Swallow parse errors – network failures already propagate to callers
  }

  return res;
};

/* ------------------------------------------------------------
 *  EVENTSOURCE WRAPPER – listen for immediate connection failure
 * ----------------------------------------------------------*/
const OriginalEventSource = window.EventSource;
function PatchedEventSource(url, options) {
  const es = new OriginalEventSource(url, options);

  pushNetworkEntry({
    type: 'eventsource.open',
    url: String(url),
    withCredentials: !!options?.withCredentials,
  });

  // Store the original onerror descriptor so we can properly intercept assignments
  const originalDescriptor = Object.getOwnPropertyDescriptor(es, 'onerror') ||
    Object.getOwnPropertyDescriptor(OriginalEventSource.prototype, 'onerror');

  let userErrorHandler = null;

  // Our quota detection handler
  const quotaErrorHandler = (evt) => {
    pushNetworkEntry({
      type: 'eventsource.error',
      url: String(url),
      readyState: es.readyState,
      eventType: evt?.type || 'error',
    });

    console.log('[EventSource Interceptor] Error event:', {
      type: evt.type,
      readyState: es.readyState,
      url: url,
      timestamp: new Date().toISOString()
    });

    // Check for immediate connection closure (quota exhausted)
    if (es.readyState === OriginalEventSource.CLOSED) {
      console.log('[EventSource Interceptor] Connection closed immediately - triggering quotaExceeded');
      window.dispatchEvent(new Event('quotaExceeded'));
    }

    // Check for connection failure during CONNECTING state
    // This often indicates server-side errors like 429, 403, 500, etc.
    if (evt.type === 'error' && es.readyState === OriginalEventSource.CONNECTING) {
      console.log('[EventSource Interceptor] Connection failed during CONNECTING state - triggering quotaExceeded');
      window.dispatchEvent(new Event('quotaExceeded'));
    }
  };

  // Override the onerror property to chain handlers
  Object.defineProperty(es, 'onerror', {
    get() {
      return userErrorHandler;
    },
    set(handler) {
      userErrorHandler = handler;
      // Set the actual onerror to call both our quota handler and the user's handler
      if (originalDescriptor && originalDescriptor.set) {
        originalDescriptor.set.call(es, (evt) => {
          quotaErrorHandler(evt);
          if (userErrorHandler) userErrorHandler(evt);
        });
      } else {
        // Fallback if descriptor isn't available
        OriginalEventSource.prototype.onerror = (evt) => {
          quotaErrorHandler(evt);
          if (userErrorHandler) userErrorHandler(evt);
        };
      }
    },
    configurable: true
  });

  // Handle initial onerror from options
  if (options?.onerror) {
    es.onerror = options.onerror;
  }

  return es;
}
PatchedEventSource.prototype = OriginalEventSource.prototype;
window.EventSource = PatchedEventSource;
